import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import { reportService, ReportServiceErrors } from '../../../../application/ReportService';
import { PendingReportCreate, reportQueueService } from '../../../../application/ReportsQueueService';
import { Route } from '../../../../domain/route/Route';
import { Stop } from '../../../../domain/stop/Stop';
import { useCaptureEvents } from '../../../../hooks/useCaptureEvents';
import { useNetwork } from '../../../../hooks/useNetwork';
import { StopReportCreateMapper } from '../../../../mappers/report/StopReportCreateMapper';
import { RouteActionType } from '../../route/context/actions';
import { useRouteContext } from '../../route/context/context';
import { getRouteMutationKey } from '../../route/hooks';
import { reportSyncSingleton } from './reportSyncSingleton';
import { usePendingReports } from './usePendingReports';

export const useSyncPendingReports = () => {
  const [isSyncingState, setIsSyncingState] = useState(false); // just for UI
  const { isOnline } = useNetwork();
  const { refetch } = usePendingReports();
  const { routeDispatch } = useRouteContext();
  const { captureCreateReport } = useCaptureEvents();
  const queryClient = useQueryClient();

  // Helper: Update route in cache and context
  const updateRouteWithReport = useCallback(
    (routeId: string, stopId: string, report: any, stopReportId: string) => {
      const routeMutationKey = getRouteMutationKey({ routeId });
      const previousQueryContent = queryClient.getQueryData<Route>(routeMutationKey);
      if (!previousQueryContent) return;

      previousQueryContent.stops = previousQueryContent.getSortedStopList({}).map((s) => {
        if (s.id === stopId) {
          report.id = stopReportId;
          const stopDummyReport = StopReportCreateMapper.toDummyReport(report, stopReportId);
          return Stop.create(
            {
              ...s.props,
              report: stopDummyReport,
            },
            s.id
          );
        }
        return s;
      });

      queryClient.setQueryData<Route>(routeMutationKey, previousQueryContent);
      routeDispatch({ type: RouteActionType.FETCH_ROUTE_SUCCESS, payload: previousQueryContent });
    },
    [queryClient, routeDispatch]
  );

  // Helper: Process a single report
  const processPendingReport = useCallback(
    async (report: PendingReportCreate) => {
      const { id, data: reportData, error } = report;
      if (!error || error === ReportServiceErrors.RetryError) {
        try {
          const response = await reportService.retryCreateReport(reportData);
          if (!response.queued) {
            captureCreateReport({
              stopReport: response.stopReport,
              status: response.stopStatus,
              isOffline: true,
              hasLocation: reportData.location ? true : false,
              hasCreatedTasks: (reportData.reportData?.temporalReportTasks?.length ?? 0) > 0 ? true : false,
            });
            await reportQueueService.removeReport(id);
            if (response.stopReport.route_id) {
              updateRouteWithReport(response.stopReport.route_id, reportData.stopId, report, response.stopReport.id);
            }
          }
        } catch (err: any) {
          await reportQueueService.updateReportError(id, err.message);
          if (err.message !== ReportServiceErrors.RetryError) {
            await reportQueueService.removeReport(id);
          }
        }
      }
    },
    [updateRouteWithReport]
  );

  // Function to sync all pending reports
  const syncAllPending = useCallback(async () => {
    await reportSyncSingleton.sync(async () => {
      setIsSyncingState(true);
      const pending = await reportQueueService.getPendingReports();

      for (const report of pending) {
        await processPendingReport(report);
      }

      await refetch();
      setIsSyncingState(false);
    });
  }, [refetch]);

  useEffect(() => {
    if (isOnline && !reportSyncSingleton.isSyncing()) {
      syncAllPending();
    }
  }, [isOnline, syncAllPending]);

  const discardAllPending = useCallback(async () => {
    const pending = await reportQueueService.getPendingReports();
    for (const report of pending) {
      await reportQueueService.removeReport(report.id);
    }
    await refetch();
    setIsSyncingState(false);
  }, [refetch]);

  return {
    isSyncing: isSyncingState,
    syncAllPending,
    discardAllPending,
  };
};
// This hook provides functionality to sync pending reports when the network is back online or manually triggered.
// It manages the syncing state and handles the logic for processing each pending report.
